/* MyCommissionWidget — Privacy-first in-cashier earnings panel.
 *
 * Hidden by default to keep commission info off the customer-facing till
 * screen. The active salesperson clicks the small eye/coin button to
 * peek at their figures. The peek auto-dismisses after 8s or on any
 * click outside the popover (so a customer walking up doesn't see it).
 *
 * Shows: GP they've personally generated, projected commission, plain
 * explanation of the rules + the profit-guardrail warning.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Coins, Info, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const AUTO_HIDE_MS = 8000;

const fmt = (v) =>
  `R ${Number(v || 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function MyCommissionWidget({ salesperson }) {
  const [row, setRow] = useState(null);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const popRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!salesperson?.id && !salesperson?.name) {
      setRow(null); setMeta(null); setOpen(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/api/commission/live-per-employee`, {
          params: { ignore_targets: "true" },
          timeout: 12000,
        });
        if (cancelled) return;
        const payouts = res.data?.payouts || [];
        const match = payouts.find((p) => {
          if (salesperson.id && p.employee_id === salesperson.id) return true;
          if (salesperson.marketing_number && p.marketing_number &&
              String(p.marketing_number) === String(salesperson.marketing_number)) return true;
          const pname = (p.name || "").toLowerCase().trim();
          const sname = (salesperson.name || "").toLowerCase().trim();
          return pname && sname && pname === sname;
        });
        setRow(match || null);
        setMeta({
          metrics: res.data?.metrics,
          config: res.data?.config_snapshot,
          guardrail: res.data?.guardrail,
        });
      } catch (_e) { /* keep last good value */ }
    };
    fetchData();
    const id = setInterval(fetchData, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [salesperson?.id, salesperson?.name, salesperson?.marketing_number]);

  // Auto-hide timer + outside-click handler — both important for privacy.
  useEffect(() => {
    if (!open) return;
    timerRef.current = setTimeout(() => setOpen(false), AUTO_HIDE_MS);
    const onClickAway = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    // Capture phase so we fire even before other handlers
    document.addEventListener("mousedown", onClickAway, true);
    document.addEventListener("touchstart", onClickAway, true);
    return () => {
      clearTimeout(timerRef.current);
      document.removeEventListener("mousedown", onClickAway, true);
      document.removeEventListener("touchstart", onClickAway, true);
    };
  }, [open]);

  if (!salesperson?.id && !salesperson?.name) return null;

  const gpGenerated = row?.layer2_gp_contribution ?? 0;
  const potentialPay = row?.total ?? 0;
  const storeLoss = (meta?.metrics?.net_profit ?? 0) <= 0;
  const sharePct = meta?.config?.layer2?.share_pct ?? 0;
  const flatBonus = meta?.config?.layer1?.bonus_per_staff ?? 0;
  const l1Target = meta?.config?.layer1?.monthly_gp_target ?? 0;

  return (
    <div className="mx-2 mt-1 mb-0 flex justify-end relative">
      {/* Tiny private button — no figures on screen until clicked */}
      <button
        onClick={() => { setOpen((v) => !v); setExpanded(false); }}
        className="flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 rounded px-2 py-0.5 border border-emerald-200 transition"
        data-testid="my-commission-toggle"
        title="Tap to peek at your month-to-date earnings (auto-closes after 8 s)"
      >
        <Coins className="h-3.5 w-3.5" />
        My earnings
      </button>

      {open && (
        <div
          ref={popRef}
          className="absolute right-2 top-full mt-1 z-50 w-[360px] max-w-[calc(100vw-1rem)] bg-white border-2 border-emerald-300 rounded-md shadow-xl p-3 text-xs"
          data-testid="my-commission-popover"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="text-sm font-semibold text-emerald-800">
              {salesperson.name || "You"} · month so far
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-700 p-1 -mt-1 -mr-1"
              data-testid="my-commission-close"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="bg-emerald-50 rounded p-2">
              <div className="text-[10px] text-gray-600 uppercase tracking-wide">GP you generated</div>
              <div className="text-base font-bold text-emerald-800">{fmt(gpGenerated)}</div>
            </div>
            <div className="bg-amber-50 rounded p-2">
              <div className="text-[10px] text-gray-600 uppercase tracking-wide">Possible commission</div>
              <div className="text-base font-bold text-amber-800">{fmt(potentialPay)}</div>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
            className="text-[11px] text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <Info className="h-3 w-3" />
            {expanded ? "Hide explanation" : "How is this calculated?"}
          </button>

          {expanded && (
            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1 text-gray-700">
              <p>
                <strong>GP generated</strong> is the gross profit (selling price minus cost) of
                every item you've rung up under your name this month.
              </p>
              <p>
                <strong>Possible commission</strong> = <strong>{sharePct}% of your GP</strong>
                {flatBonus > 0 && (
                  <>
                    {" "}+ <strong>{fmt(flatBonus)} flat bonus</strong> when the store hits
                    {l1Target > 0 ? <> R {Number(l1Target).toLocaleString("en-ZA")} total GP this month</> : <> its monthly target</>}
                  </>
                )}.
              </p>
              <p className="text-amber-700">
                ⚠ <strong>Only paid if the store ends the month profitable.</strong>{" "}
                {storeLoss
                  ? "Right now we're at a loss — every sale helps push us back into profit!"
                  : "The store is currently profitable — keep it going."}
              </p>
              <p className="text-gray-500 italic text-[10px]">
                Final amounts are confirmed at month-end.
              </p>
            </div>
          )}

          <div className="mt-2 pt-1 border-t border-gray-100 text-[10px] text-gray-400 italic text-center">
            Auto-closes after 8 seconds for privacy
          </div>
        </div>
      )}
    </div>
  );
}
